import React, { useState, useMemo } from 'react';
import { 
  PencilIcon, 
  EyeIcon, 
  TrashIcon, 
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserIcon,
  CalendarIcon,
  TagIcon,
  DocumentTextIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { useBlogAuthors } from '../hooks/useBlogAuthors';
import { formatDateTime } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';

const BlogTable = ({ 
  documents, 
  onEdit, 
  onView, 
  onDelete, 
  loading 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);

  // Use blog authors hook
  const { getAuthorName, getAuthorId, loading: authorsLoading } = useBlogAuthors(documents);
  
  // Use auth context for role-based access control
  const { currentUser, userRole, isAdmin } = useAuth();
  
  // Check if user can edit/delete a specific blog
  const canEditBlog = (blog) => {
    if (isAdmin) return true; // Admins can edit all blogs
    if (userRole === 'author' && currentUser) {
      // Check if user owns this blog by userId or author field
      const isOwner = blog.userId === currentUser.uid || 
                     blog.author === currentUser.uid ||
                     blog.author === currentUser.email ||
                     blog.author === currentUser.displayName;
      
      return isOwner;
    }
    return false;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'published': return CheckCircleIcon;
      case 'draft': return ClockIcon;
      case 'archived': return ExclamationTriangleIcon;
      default: return ClockIcon;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'text-green-700 bg-green-100';
      case 'draft': return 'text-yellow-700 bg-yellow-100';
      case 'archived': return 'text-gray-700 bg-gray-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const formatDate = (timestamp) => {
    return formatDateTime(timestamp, 'Unknown');
  };

  const getReadingTime = (content, wordCount) => {
    const words = wordCount || (content ? content.split(/\s+/).length : 0);
    const minutes = Math.ceil(words / 200); // Average reading speed
    return `${minutes} min read`;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const filteredAndSortedDocuments = useMemo(() => {
    let filtered = documents.filter(doc => {
      const matchesSearch = !searchTerm || 
        doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.author?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    // Sort documents
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle timestamps
      if (sortBy === 'timestamp' || sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aValue = aValue?._seconds || 0;
        bValue = bValue?._seconds || 0;
      }

      // Handle strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [documents, searchTerm, statusFilter, sortBy, sortOrder]);

  const statusCounts = useMemo(() => {
    const counts = { all: documents.length, published: 0, draft: 0, archived: 0 };
    documents.forEach(doc => {
      if (doc.status && counts.hasOwnProperty(doc.status)) {
        counts[doc.status]++;
      }
    });
    return counts;
  }, [documents]);

  if (loading) {
    return (
      <div className="bg-white shadow-xl rounded-xl border border-gray-200">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="grid grid-cols-6 gap-4">
                  <div className="h-4 bg-gray-200 rounded col-span-2"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-xl rounded-xl border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <DocumentTextIcon className="h-8 w-8 mr-3 text-indigo-600" />
              Blog Posts
            </h2>
            <p className="text-gray-600 mt-1">
              Manage your blog content and posts
            </p>
          </div>
          
          {/* Stats */}
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{statusCounts.all}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{statusCounts.published}</div>
              <div className="text-xs text-gray-500">Published</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{statusCounts.draft}</div>
              <div className="text-xs text-gray-500">Drafts</div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mt-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search blogs by title, content, or author..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium transition-colors ${
                showFilters ? 'bg-indigo-50 text-indigo-700 border-indigo-300' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="all">All ({statusCounts.all})</option>
                  <option value="published">Published ({statusCounts.published})</option>
                  <option value="draft">Draft ({statusCounts.draft})</option>
                  <option value="archived">Archived ({statusCounts.archived})</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="timestamp">Date Created</option>
                  <option value="updatedAt">Last Updated</option>
                  <option value="title">Title</option>
                  <option value="status">Status</option>
                  <option value="wordCount">Word Count</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {filteredAndSortedDocuments.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No blog posts found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Get started by creating your first blog post'
              }
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Post
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Author
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedDocuments.map((doc) => {
                const StatusIcon = getStatusIcon(doc.status || 'draft');
                
                return (
                  <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                    {/* Post Info */}
                    <td className="px-6 py-4">
                      <div className="flex items-start space-x-3">
                        {/* Featured Image */}
                        <div className="flex-shrink-0">
                          {doc.link ? (
                            <img
                              src={doc.link}
                              alt=""
                              className="h-12 w-12 rounded-lg object-cover border border-gray-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center ${doc.link ? 'hidden' : ''}`}>
                            <PhotoIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="text-sm font-medium text-gray-900">
                              {truncateText(doc.title, 60) || 'Untitled Post'}
                            </div>
                            {currentUser && (doc.userId === currentUser.uid || 
                                            doc.author === currentUser.uid || 
                                            doc.author === currentUser.email || 
                                            doc.author === currentUser.displayName) && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Your Post
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 mb-2">
                            {truncateText(doc.description, 80)}
                          </div>
                          
                          {/* Tags */}
                          {doc.tags && (
                            <div className="flex flex-wrap gap-1">
                              {(typeof doc.tags === 'string' ? doc.tags.split(',') : doc.tags || [])
                                .slice(0, 3)
                                .map((tag, index) => (
                                  <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                    <TagIcon className="h-3 w-3 mr-1" />
                                    {tag.trim()}
                                  </span>
                                ))}
                              {(typeof doc.tags === 'string' ? doc.tags.split(',') : doc.tags || []).length > 3 && (
                                <span className="text-xs text-gray-500">
                                  +{(typeof doc.tags === 'string' ? doc.tags.split(',') : doc.tags || []).length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(doc.status || 'draft')}`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {doc.status || 'draft'}
                      </div>
                    </td>

                    {/* Author */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <div className="truncate max-w-[120px]">
                          {authorsLoading ? (
                            <div className="animate-pulse bg-gray-200 h-4 w-16 rounded"></div>
                          ) : (
                            <div>
                              <div className="font-medium">{getAuthorName(getAuthorId(doc))}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {getAuthorId(doc) ? getAuthorId(doc).substring(0, 8) + '...' : 'No ID'}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Stats */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        <div>{doc.wordCount || (doc.content ? doc.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0)} words</div>
                        <div>{getReadingTime(doc.content, doc.wordCount)}</div>
                        {doc.contentType && (
                          <div className="text-xs">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                              doc.contentType === 'html' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {doc.contentType.toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Date */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-2" />
                        <div>
                          <div>{formatDate(doc.timestamp || doc.createdAt)}</div>
                          {doc.updatedAt && (
                            <div className="text-xs text-gray-400">
                              Updated: {formatDate(doc.updatedAt)}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => onView(doc)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50 transition-colors"
                          title="View"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {canEditBlog(doc) && (
                          <button
                            onClick={() => onEdit(doc)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded-md hover:bg-gray-50 transition-colors"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canEditBlog(doc) && (
                          <button
                            onClick={() => onDelete(doc.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {filteredAndSortedDocuments.length} of {documents.length} blog posts
          </div>
          <div className="text-sm text-gray-500">
            {searchTerm && `Filtered by: "${searchTerm}"`}
            {statusFilter !== 'all' && ` • Status: ${statusFilter}`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogTable;
